/**
 * `/busca?q=` — busca.
 *
 * O termo vive na query string porque a pessoa precisa poder voltar e compartilhar. Ele **nao**
 * entra no titulo do documento (REG-045, modo discricao: o titulo aparece na lista de abas e em
 * screenshot) nem em telemetria (REG-044).
 *
 * O `debounce` mantem o valor digitado separado do valor da URL: escrever na URL a cada tecla
 * empilharia uma entrada de historico por caractere, e o botao "voltar" viraria uma tortura.
 */
import { useApiQueries, type QueryOptions } from '@farmacia/api-client/query';
import type { SearchResponse } from '@farmacia/contracts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { useMinhaLoja } from '../loja/contexto.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import { useAdicionarAoCarrinho } from '../carrinho/useAdicionarAoCarrinho.js';
import { Busca, DEBOUNCE_DA_BUSCA_MS, deveBuscar } from '../telas/Busca.jsx';

interface BuscaSearch {
  readonly q?: string;
  readonly categoria?: string;
}

export const Route = createFileRoute('/busca')({
  validateSearch: (bruto: Record<string, unknown>): BuscaSearch => ({
    ...(typeof bruto.q === 'string' ? { q: bruto.q } : {}),
    ...(typeof bruto.categoria === 'string' ? { categoria: bruto.categoria } : {}),
  }),
  component: TelaDeBusca,
});

/**
 * A opcao de reserva quando nao ha filial.
 *
 * Ela existe porque `useQuery` precisa de uma chave estavel mesmo desabilitado — e o `queryFn`
 * **rejeita**: se algum dia alguem ligar o `enabled` sem ligar a filial, o resultado e um erro
 * alto no proprio app, e nao um `422` do servidor exibido como "Nada encontrado".
 */
const OPCOES_SEM_FILIAL: QueryOptions<SearchResponse> = {
  queryKey: ['catalog', 'search', 'sem-filial'],
  queryFn: () =>
    Promise.reject(new Error('Busca sem filial escolhida: a consulta nao deveria sair.')),
  staleTime: 0,
  gcTime: 0,
};

function TelaDeBusca(): ReactNode {
  const { q } = Route.useSearch();
  const navegar = Route.useNavigate();
  const queries = useApiQueries();
  const { storeId, hidratando, abrirSeletor } = useMinhaLoja();
  const { conexao } = usePwa();
  const carrinho = useAdicionarAoCarrinho();

  const [termo, setTermo] = useState(q ?? '');
  const [termoEstavel, setTermoEstavel] = useState(termo);

  useEffect(() => {
    const id = setTimeout(() => { setTermoEstavel(termo); }, DEBOUNCE_DA_BUSCA_MS);
    return () => { clearTimeout(id); };
  }, [termo]);

  // A URL so e atualizada com o termo JA estabilizado, e com `replace` — uma entrada de
  // historico por busca, nao por tecla.
  useEffect(() => {
    if (termoEstavel === (q ?? '')) return;
    void navegar({ search: termoEstavel === '' ? {} : { q: termoEstavel }, replace: true });
  }, [termoEstavel, q, navegar]);

  /*
   * `storeId ?? ''` era a origem do `422`.
   *
   * `SearchQuerySchema` exige `storeId`; a string vazia passava pelo tipo, entrava na chave de
   * cache e — a um `enabled` de distancia — saia na query string. O `enabled` era a unica
   * protecao, e uma protecao que depende de nao esquecer uma linha nao e protecao.
   *
   * Agora a opcao de query **so existe quando ha filial**: sem `storeId` nao ha objeto para o
   * `useQuery` chamar, entao nao ha requisicao possivel — nem por engano, nem por um `enabled`
   * que alguem inverta no futuro. O `queryKey` de reserva nunca e consultado (`enabled: false`)
   * e existe porque o hook precisa de uma chave estavel.
   */
  const opcoes: QueryOptions<SearchResponse> =
    storeId === null ? OPCOES_SEM_FILIAL : queries.catalog.search({ q: termoEstavel, storeId });

  const consulta = useQuery({
    ...opcoes,
    enabled: storeId !== null && deveBuscar(termoEstavel),
  });

  return (
    <Busca
      termo={termo}
      aoMudarTermo={setTermo}
      resultados={consulta.data?.data}
      carregando={consulta.isFetching}
      erro={consulta.error}
      aoTentarDeNovo={() => void consulta.refetch()}
      offline={conexao === 'OFFLINE'}
      modoEconomia={conexao === 'DEGRADED'}
      // Enquanto a copia duravel do IndexedDB nao respondeu, nao afirmamos que falta filial:
      // quem acabou de sair da conta tem o espelho sincrono apagado e a filial no `kv`.
      semLoja={storeId === null && !hidratando}
      aoEscolherLoja={abrirSeletor}
      aoAdicionar={carrinho.adicionar}
      adicionandoId={carrinho.produtoEmVoo}
      erroAoAdicionar={carrinho.erroDe}
    />
  );
}
